import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllMyMintSpaces } from "../api/mintSpace";
import { sortMintSpacesByRecent } from "../utils/mintSpaceRecent";
import { useTranslation } from "../context/TranslationContext";
import { getBusinessCategoryLabel } from "../constants/businessCategories";
import Avatar from "../components/ds/Avatar";
import EmptyState from "../components/ds/EmptyState";
import Icon from "../components/ds/Icon";
import Page from "../components/ds/Page";
import PageHeader from "../components/ds/PageHeader";

const ROW_STATE = "relative after:pointer-events-none after:absolute after:inset-0 after:bg-label-primary after:opacity-0 after:transition-opacity hover:after:opacity-[var(--state-hover)] active:after:opacity-[var(--state-pressed)]";

export default function JoinedMintsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getAllMyMintSpaces();
        if (cancelled) return;
        const joined = (result.data || []).filter((s) => s.isMember && !s.isOwner);
        setSpaces(sortMintSpacesByRecent(joined));
      } catch {
        if (!cancelled) setSpaces([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Page>
      <PageHeader
        title={t("navigation.joinedMints") || "Joined Mints"}
        subtitle={t("joinedMints.subtitle") || "Businesses you've joined — most recent activity first."}
      />

      {loading ? (
        <p className="text-subheadline text-label-secondary">{t("common.loading") || "Loading..."}</p>
      ) : spaces.length === 0 ? (
        <EmptyState icon="users" title={t("joinedMints.empty") || "You haven't joined any businesses yet."} />
      ) : (
        <ul className="flex flex-col overflow-hidden rounded-radius-lg bg-bg-raised">
          {spaces.map((space) => (
            <li key={space._id} className="border-b border-separator-default last:border-b-0">
              <button
                type="button"
                onClick={() => navigate(`/mint-space/${space._id}`)}
                className={`flex w-full items-center gap-space-12 px-space-16 py-space-12 text-left outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--color-focus-ring)] ${ROW_STATE}`}
              >
                <Avatar size="l" src={space.logo} name={space.name} />
                <span className="flex min-w-0 flex-1 flex-col gap-space-4">
                  <span className="truncate text-headline text-label-primary">{space.name}</span>
                  {space.category && (
                    <span className="truncate text-footnote text-label-secondary">{getBusinessCategoryLabel(t, space.category)}</span>
                  )}
                </span>
                <Icon name="chevron-right" size={20} className="shrink-0 text-label-tertiary" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Page>
  );
}
